/**
 * One body of state.
 *
 * **Selection and navigation live here, not in a navigator.** That is what lets the phone render a
 * selected match as a pushed screen and the desktop as a second pane beside the list, from the same
 * state — the fleet's width-decides-layout rule, which only works if there is one truth to arrange.
 */
import { create } from 'zustand';
import { api, describeError } from '@/api';
import {
  applySignals,
  fetchBets,
  fetchLeagues,
  fetchMatches,
  fetchResults,
  fetchScorecards,
  leaguesInPlay,
  refreshMatches,
  requestPrediction,
  drivePredictions,
  type LeagueRow,
  type Scorecard,
} from '@/data/matches';
import { BET_LINES, type BetKey, type Match, type PlacedBet } from '@/models';
import { getSync, setItem } from '@/storage';
import type { MatchResult } from '@/stats/types';

export const SCREENS = [
  'upcoming',
  'predictions',
  'bets',
  'live',
  'complete',
  'leagues',
  'backtest',
  'settings',
] as const;
export type Screen = (typeof SCREENS)[number];

const THEME_KEY = 'psb.theme';
let themeChosen = false;
export const themeWasChosen = () => themeChosen;

interface State {
  screen: Screen;
  theme: 'light' | 'dark';
  paired: boolean;
  loading: boolean;
  error: string | null;
  status: string;

  matches: Match[];
  bets: PlacedBet[];
  leagues: LeagueRow[];
  scorecards: Scorecard[];
  resultsByLeague: Map<string, MatchResult[]>;

  /** null = "All". Preserved across refreshes. */
  selectedLeague: string | null;
  todayOnly: boolean;
  /** The match whose detail pane is open. Width decides whether that is a pane or a screen. */
  selectedMatchId: string | null;
  /** Set by "Apply to all" and read by every stake box on the visible cards. */
  stakeAll: string;
  /** Per-line stakes, keyed `${eventId}:${betKey}`. In memory, like the desktop's. */
  stakes: Record<string, string>;
  /** Last placement message per line, keyed the same way. */
  placements: Record<string, string>;

  go: (s: Screen) => void;
  setTheme: (t: 'light' | 'dark') => void;
  toggleTheme: () => void;
  setPaired: (p: boolean) => void;
  dismissError: () => void;
  selectLeague: (l: string | null) => void;
  toggleToday: () => void;
  selectMatch: (id: string | null) => void;
  setStakeAll: (v: string) => void;
  setStake: (eventId: string, key: BetKey, value: string) => void;
  /** Writes the stake-all amount into every line of the matches passed in — the visible ones. */
  applyStakeToAll: (eventIds: string[]) => void;

  load: () => Promise<void>;
  refresh: () => Promise<void>;
  predict: (eventId: string) => Promise<void>;
  predictAll: (eventIds: string[]) => Promise<void>;
  place: (eventId: string, key: BetKey) => Promise<void>;
}

export const stakeKey = (eventId: string, key: BetKey) => `${eventId}:${key}`;

export const useStore = create<State>((set, get) => ({
  screen: 'upcoming',
  theme: (getSync(THEME_KEY) as 'light' | 'dark' | null) ?? 'dark',
  paired: false,
  loading: false,
  error: null,
  status: '',

  matches: [],
  bets: [],
  leagues: [],
  scorecards: [],
  resultsByLeague: new Map(),

  selectedLeague: null,
  todayOnly: false,
  selectedMatchId: null,
  stakeAll: '',
  stakes: {},
  placements: {},

  go: (screen) => set({ screen, selectedMatchId: null }),
  setTheme: (theme) => set({ theme }),
  toggleTheme: () => {
    const theme = get().theme === 'dark' ? 'light' : 'dark';
    themeChosen = true;
    void setItem(THEME_KEY, theme);
    set({ theme });
  },
  setPaired: (paired) => set({ paired }),
  dismissError: () => set({ error: null }),
  selectLeague: (selectedLeague) => set({ selectedLeague }),
  toggleToday: () => set({ todayOnly: !get().todayOnly }),
  selectMatch: (selectedMatchId) => set({ selectedMatchId }),
  setStakeAll: (stakeAll) => set({ stakeAll }),
  setStake: (eventId, key, value) =>
    set({ stakes: { ...get().stakes, [stakeKey(eventId, key)]: value } }),

  /**
   * Pushes the stake-all amount into every line on the matches currently SHOWN, so the league and
   * Today filters scope it — the same rule the desktop's box followed.
   */
  applyStakeToAll: (eventIds) => {
    const value = get().stakeAll.trim();
    if (!value) return;
    const next = { ...get().stakes };
    for (const id of eventIds) for (const k of BET_LINES) next[stakeKey(id, k)] = value;
    set({ stakes: next });
  },

  /**
   * The whole load, cached-first: matches and the ledger land immediately, then the results the
   * signals need arrive per league and the cards fill in. Nothing here blocks on Polymarket.
   */
  async load() {
    if (!api.paired) return;
    set({ loading: true, error: null });
    try {
      const [matches, bets, scorecards] = await Promise.all([
        fetchMatches(),
        fetchBets(),
        fetchScorecards(),
      ]);
      set({ matches, bets, scorecards, status: describeStatus(matches) });

      // Only the leagues actually on the board, so a phone is not made to hold 10k rows it has no
      // use for. Already-loaded leagues are not re-fetched.
      const have = get().resultsByLeague;
      const wanted = leaguesInPlay(matches).filter((c) => !have.has(c));
      if (wanted.length) {
        const loaded = await Promise.all(
          wanted.map(async (code) => [code, await fetchResults(code)] as const),
        );
        const next = new Map(have);
        for (const [code, rows] of loaded) next.set(code, rows);
        set({ resultsByLeague: next });
      }

      set({ matches: [...applySignals(get().matches, get().resultsByLeague, get().scorecards)] });

      // The Leagues screen wants every baseline, not just the ones on the board. Cheap: the Worker
      // aggregates it in SQL.
      fetchLeagues()
        .then((leagues) => set({ leagues }))
        .catch(() => undefined);
    } catch (e) {
      set({ error: describeError(e) });
    } finally {
      set({ loading: false });
    }
  },

  async refresh() {
    set({ loading: true, error: null, status: 'Refreshing from Polymarket…' });
    try {
      await refreshMatches();
      await get().load();
    } catch (e) {
      set({ error: describeError(e), loading: false });
    }
  },

  /**
   * Queues a prediction and nudges the driver.
   *
   * The row goes to `queued` immediately and stays there until the driver finishes — which may be
   * a while, because the PC executor can be asleep. That is a normal state and the card says so.
   */
  async predict(eventId) {
    const mark = (jobStatus: Match['jobStatus']) =>
      set({
        matches: get().matches.map((m) => (m.eventId === eventId ? { ...m, jobStatus } : m)),
      });
    mark('queued');
    try {
      await requestPrediction(eventId);
      await drivePredictions().catch(() => undefined); // best effort; the cron picks it up anyway
      await get().load();
    } catch (e) {
      set({ error: describeError(e) });
      mark(null);
    }
  },

  /** Queues every shown match that has no prediction yet. The driver runs them one at a time. */
  async predictAll(eventIds) {
    set({ error: null });
    for (const id of eventIds) {
      try {
        await requestPrediction(id);
      } catch (e) {
        set({ error: describeError(e) });
        break;
      }
    }
    await get().load();
    void drivePredictions().catch(() => undefined);
  },

  /**
   * Records a bet INTENT. This does not place anything — the Ed25519 money key lives on the PC and
   * never leaves it, so `trader/` claims the intent, prices it against the live book and executes.
   *
   * The id is minted here rather than by the server, which is what makes a double tap on a flaky
   * connection resolve to one bet instead of two.
   */
  async place(eventId, key) {
    const sk = stakeKey(eventId, key);
    const stake = Number.parseFloat(get().stakes[sk] ?? '');
    if (!(stake > 0)) return;

    const say = (note: string) => set({ placements: { ...get().placements, [sk]: note } });
    say('Queued for the trader…');
    try {
      await api.post('/api/bet-intents', {
        id: `${eventId}-${key}-${Date.now()}`,
        eventId,
        betKey: key,
        stake,
      });
      say(`$${stake.toFixed(2)} queued. The PC prices and places it — the key never leaves it.`);
    } catch (e) {
      say(`Could not queue: ${describeError(e)}`);
    }
  },
}));

function describeStatus(matches: Match[]): string {
  const now = Date.now();
  const upcoming = matches.filter((m) => {
    const t = Date.parse(m.kickoffUtc);
    return Number.isFinite(t) && t + 3 * 3600_000 > now;
  }).length;
  const predicted = matches.filter((m) => m.prediction).length;
  return `${upcoming} upcoming · ${predicted} predicted · ${matches.length} cached`;
}
